import { useAtom } from 'jotai'
import { useLastUsedFaxNumber } from '~/atoms/FaxInteractionState'
import { useUserState } from '~/atoms/UserState'
import { FaxOperation } from '~/hooks/toast/eventHandlers/faxToastEventHandler'
import { useEmitterService } from '~/hooks/useEmitterService'
import { useResendFax, useSendFax } from '~/queries/faxQueries'
import {
  NotificationDisposition,
  NotificationDomain,
  NotificationEmitterEventType,
} from '~/services/EventEmitter/types/NotificationEmitterTypes'
import { startTimeWeightedProgressTimer } from '~/utils/common'

import type { PhoneNumber } from '@mangovoice/types'

import { stateDefault } from './constants'
import { FaxSendingStep } from './enums'
import type { Mutators, PhoneNumberListState, State, ViewModel } from './types'
import { makeMutableState, makeStateAtom } from './utils'

let stateMutable = makeMutableState(stateDefault)

const stateJotaiDefault = makeStateAtom(stateMutable)

export const useNewFaxViewModel = (): ViewModel => {
  const { pbxId } = useUserState()
  const [viewModelState, setViewModelState] = useAtom(stateJotaiDefault)
  const [lastUsedFaxNumber, setLastUsedFaxNumber] = useLastUsedFaxNumber()
  const { emit } = useEmitterService()

  const getValidatedState = (newFaxViewModelState: State) => {
    const validatedState = {
      ...newFaxViewModelState,
    }

    if (validatedState.faxSendingStep === FaxSendingStep.Sending) {
      //if a fax wasn't being sent, but now is, reset the progress
      if (stateMutable.faxSendingStep !== FaxSendingStep.Sending) {
        validatedState.faxSendingProgress = 0
      }
    }

    const { phoneNumberListState } = validatedState
    if (!phoneNumberListState.hasNextPage) {
      validatedState.phoneNumberListState.nextPageOffset = 0
    }

    if (validatedState.selectedPhoneNumber) {
      validatedState.fromNumber = validatedState.selectedPhoneNumber.number
    }

    return validatedState
  }

  const mutators: Mutators = {
    set phoneNumberListState(phoneNumberListState: PhoneNumberListState) {
      mutators.newFaxViewModelState = {
        ...stateMutable,
        phoneNumberListState,
      }
    },
    set faxSendingStep(faxSendingStep: FaxSendingStep) {
      mutators.newFaxViewModelState = {
        ...stateMutable,
        faxSendingStep,
      }
    },
    set faxSendingProgress(faxSendingProgress: number) {
      mutators.newFaxViewModelState = {
        ...stateMutable,
        faxSendingProgress,
      }
    },
    set sendEmailConfirmation(sendEmailConfirmation: boolean) {
      mutators.newFaxViewModelState = {
        ...stateMutable,
        sendEmailConfirmation,
      }
    },
    set emailConfirmations(emailConfirmations: string[]) {
      mutators.newFaxViewModelState = {
        ...stateMutable,
        emailConfirmations,
      }
    },
    set selectedPhoneNumber(selectedPhoneNumber: PhoneNumber | null) {
      mutators.newFaxViewModelState = {
        ...stateMutable,
        selectedPhoneNumber,
      }
    },
    set selectedFiles(selectedFiles: File[]) {
      mutators.newFaxViewModelState = {
        ...stateMutable,
        selectedFiles,
      }
    },
    set fromNumber(fromNumber: string) {
      mutators.newFaxViewModelState = {
        ...stateMutable,
        fromNumber,
      }
    },
    set toNumber(toNumber: string) {
      mutators.newFaxViewModelState = {
        ...stateMutable,
        toNumber,
      }
    },
    set resendId(resendId: number) {
      mutators.newFaxViewModelState = {
        ...stateMutable,
        resendId,
      }
    },
    set newFaxViewModelState(newFaxViewModelState: State) {
      const validatedState = getValidatedState(newFaxViewModelState)

      stateMutable = validatedState
      setViewModelState(validatedState)
    },
  }

  const sendFaxStatusNotification = () => {
    const disposition =
      stateMutable.faxSendingStep === FaxSendingStep.Success
        ? NotificationDisposition.Success
        : NotificationDisposition.Error

    emit(NotificationEmitterEventType.PROCESS_UPDATED, {
      disposition: disposition,
      domain: NotificationDomain.FAX,
      operation: FaxOperation.Send,
    })
  }

  const { mutate: sendFaxInternal } = useSendFax({
    onSuccess: async () => {
      while (stateMutable.faxSendingStep !== FaxSendingStep.SendComplete) {
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      mutators.faxSendingStep = FaxSendingStep.Success
      setLastUsedFaxNumber(viewModelState.selectedPhoneNumber)
      sendFaxStatusNotification()
    },
    onError: () => {
      mutators.faxSendingStep = FaxSendingStep.Failure
      setLastUsedFaxNumber(null)
      sendFaxStatusNotification()
    },
  })

  const { mutate: resendFaxInternal } = useResendFax({
    onSuccess: () => {
      mutators.faxSendingStep = FaxSendingStep.Success
      setLastUsedFaxNumber(viewModelState.selectedPhoneNumber)
      sendFaxStatusNotification()
    },
    onError: () => {
      mutators.faxSendingStep = FaxSendingStep.Failure
      setLastUsedFaxNumber(null)
      sendFaxStatusNotification()
    },
  })

  const handleUpdateSendFaxProgress = (progress: number) => {
    mutators.faxSendingProgress = Math.min(progress, 1) * 100
  }

  const sendFax = (
    fromNumber: string,
    toNumber: string,
    files: File[],
    emailConfirmations: string[],
  ) => {
    //only send one fax at a time - send an event notification about this?
    if (viewModelState.faxSendingStep === FaxSendingStep.Sending) {
      return
    }

    mutators.faxSendingStep = FaxSendingStep.Sending

    let progress = 0
    sendFaxInternal({
      emailConfirmations: viewModelState.sendEmailConfirmation
        ? emailConfirmations
        : [],
      files,
      fromPhoneNumber: fromNumber,
      onProgress: (p) => (progress = p.loaded / p.total),
      pbxId: pbxId ?? 0,
      toPhoneNumber: toNumber,
    })

    startTimeWeightedProgressTimer({
      minTimeMs: 2500,
      getRawProgress: () => progress,
      onProgressUpdated: (p) => handleUpdateSendFaxProgress(p),
      onFinished: () => {
        if (stateMutable.faxSendingStep !== FaxSendingStep.Sending) {
          return
        }

        mutators.faxSendingStep = FaxSendingStep.SendComplete
      },
      fauxEndWaitTimeMs: 500,
    })
  }

  const resendFax = (resendId: number) => {
    //UX: only send one fax at a time - send an event notification about this?
    if (viewModelState.faxSendingStep === FaxSendingStep.Sending) {
      return
    }
    mutators.faxSendingStep = FaxSendingStep.Sending
    resendFaxInternal(resendId)
  }

  const resetState = () => {
    mutators.newFaxViewModelState = stateDefault
  }

  return {
    actions: {
      sendFax,
      resendFax,
      resetState,
    },
    mutators,
    state: {
      ...viewModelState,
      lastUsedFaxNumber,
    },
  }
}
